import React, { useState, useEffect } from 'react'
import Page from '../../components/Page'
import './view.css'
import { useParams, useSearchParams } from 'react-router-dom'
import { apiConstants, getGeneric, post, postGeneric } from '../../common/constants'
import { formatCurrency, formatDate, formatDate1 } from '../../common/util'
import AppointmentHistoryView from '../AppointmentDeliveriesV2/shared/AppointmentHistoryView'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  CardHeader,
  Stack,
  Chip,
  Grid,
  Typography,
  Box,
  Link,
  Tooltip,
  IconButton,
  Divider,
  CircularProgress,
} from '@mui/material'
import EventIcon from '@mui/icons-material/Event'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import DescriptionIcon from '@mui/icons-material/Description'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import { downloadPOD, podAvailable } from './generatePOD'
import StatusBanner from './detail/StatusBanner'
import SummaryCards from './detail/SummaryCards'
import CarrierLogsSection from './detail/CarrierLogsSection'
import { InfoRow as KitInfoRow, SectionCard as KitSectionCard } from './detail/detailKit'
import { categoryLabel } from '../B2BOrder/ExtraDocsEditor'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import PhoneIcon from '@mui/icons-material/Phone'
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn'
import { VisibilityTwoTone, VisibilityOffTwoTone } from '@mui/icons-material'

import { DataGrid } from '@mui/x-data-grid'
import {
  addressStringWithPrefix,
  maskAddress,
  maskEmail,
  maskPhoneNumber,
  parseDateAndTime,
  parseTime,
  shouldMaskData,
  shouldMaskDataReverse,
} from '../../helpers/UtilityHelper'
import ProzoDataGrid from '../../common/ProzoDataGrid'
import MUIModal from '../../components/MuiModal'
import PageTitle from '../../components/PageTitle'
import OrderTimeline from './OrderTimeline'
import AddressQualityCard from './AddressQualityCard'
import RtoRiskCard from './RtoRiskCard'
import { useGetAllCourierPartnersCachedQuery } from '../../redux/commonApiCalls'
import { ProductImageThumbs, imagesOf } from '../../common/ImageLightbox'
import { useSelector } from 'react-redux'
import QcDetails from '../B2BOrder/QcDetails'
import VisibilityIcon from '@mui/icons-material/Visibility'
import PTOrderTimeline from './PTOrderTimeline'
import { useSnackbar } from 'notistack'
import jwt_decode from 'jwt-decode'
import { getToken, getAccess } from '../../helpers/ReduxHelpers'
import Loader from '../../common/Loader'
import PIIDetails from '../../components/PIIDetails'
import CopyIcon from '../../common/CopyIcon'

// call_duration is stored in MINUTES — the same field the NDR Call Recordings page
// reads (e.g. 1 = 1 min, 1.5 = 1m 30s). This table previously treated it as seconds,
// so a one-minute call printed as "1s". Format minutes as "Xm Ys".
function formatCallDurationMin(minutes) {
  if (minutes == null || minutes === '') return '—'
  const v = Number(minutes)
  if (isNaN(v)) return '—'
  const m = Math.floor(v)
  const sec = Math.round((v - m) * 60)
  if (m > 0 && sec > 0) return `${m}m ${sec}s`
  if (m > 0) return `${m}m`
  return `${sec}s`
}

// Page-level skin: restyles every MUI Card/CardHeader on the Order Details page to the merchant
// panel's look (rounded 14px cards, light-grey header bar, bold navy title) in one place, so the
// whole page matches without editing each of the ~18 card sections individually. Cards with their
// own inline sx (e.g. AddressCard) keep their styling — inline sx wins over this descendant rule.
const detailSkinSx = {
  fontFamily: 'Poppins, sans-serif',
  '& .MuiCard-root': { borderRadius: '14px', border: '1px solid #DFE3E8', boxShadow: 'none' },
  '& .MuiCardHeader-root': { padding: '10px 20px', backgroundColor: '#F9FAFB', borderBottom: '1px solid #DFE3E8' },
  '& .MuiCardHeader-root .MuiCardHeader-title': { fontFamily: 'Poppins, sans-serif', fontSize: 13, fontWeight: 700, color: '#212B36' },
  '& .MuiCardContent-root': { padding: '14px 20px' },
}

// Merchant-style row: muted label left, semibold navy value right, hairline divider between rows.
function AddressLine({ label, value, trailing }) {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 2,
        py: 0.85,
        borderBottom: '1px solid #DFE3E8',
        '&:last-child': { borderBottom: 'none' },
      }}
    >
      <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: 12, color: '#637381', fontWeight: 500, flexShrink: 0 }}>
        {label}
      </Typography>
      <Box
        sx={{
          fontFamily: 'Poppins, sans-serif',
          fontSize: 12,
          color: '#212B36',
          fontWeight: 600,
          textAlign: 'right',
          wordBreak: 'break-word',
          '& .MuiTypography-root': { fontSize: 12, color: '#212B36' },
        }}
      >
        {value || '—'}
        {trailing}
      </Box>
    </Box>
  )
}

function AddressCard({ title, accent = false, action, children }) {
  return (
    <Card
      sx={{
        height: '100%',
        borderRadius: '14px',
        border: '1px solid',
        borderColor: accent ? '#2065D1' : '#DFE3E8',
        boxShadow: 'none',
        overflow: 'hidden',
      }}
    >
      <CardHeader
        title={title}
        action={action}
        titleTypographyProps={{
          fontFamily: 'Poppins, sans-serif',
          fontSize: 13,
          fontWeight: 700,
          color: accent ? '#103996' : '#212B36',
        }}
        sx={{ py: 1.25, px: 2.5, bgcolor: '#F9FAFB', borderBottom: '1px solid #DFE3E8', '& .MuiCardHeader-action': { m: 0, alignSelf: 'center' } }}
      />
      <CardContent sx={{ px: 2.5, py: 1.5, pb: '16px !important' }}>{children}</CardContent>
    </Card>
  )
}

const columns = [
  {
    field: 'item_name',
    headerName: 'Product name',
    flex: 1.2,
  },
  {
    field: 'sku_id',
    headerName: 'SKU',
    flex: 1,
  },
  {
    field: 'units',
    headerName: 'Qty',
    flex: 0.5,
  },
  {
    field: 'tax',
    headerName: 'Tax',
    flex: 0.5,
    // type: 'number',
    valueFormatter: (params) => {
      if (params.value == null) {
        return ''
      }

      const valueFormatted = Number(params.value).toLocaleString()
      return `${valueFormatted} %`
    },
  },
  {
    field: 'selling_price',
    headerName: 'Price',
    flex: 0.5,
    // type: "number",
    valueFormatter: (params) => {
      if (params.value == null) {
        return ''
      }

      const valueFormatted = Number(params.value).toLocaleString()
      return `₹ ${valueFormatted}`
    },
  },

  {
    field: 'item_url',
    headerName: 'Item Image',
    flex: 0.8,
    sortable: false,
    renderCell: (params) => {
      const raw = params?.row?.item_url
      const urls =
        raw && raw !== 'NA'
          ? String(raw)
              .split(',')
              .map((u) => u.trim())
              .filter(Boolean)
          : []
      return <ProductImageThumbs images={urls} size={34} empty="No image" />
    },
  },
  {
    // Catalog images uploaded in Product Master for this SKU (fetched by
    // merchantId + skuCode). Thumbnails only — click opens the in-app carousel.
    field: 'productImages',
    headerName: 'Product',
    flex: 0.8,
    sortable: false,
    renderCell: (params) => <ProductImageThumbs images={params.row.productImages} size={34} empty="No image" />,
  },
]
const columnsNPRActionLog = [
  {
    field: 'nprAction',
    hideSortIcons: true,
    headerName: 'Action Type',
    flex: 0.8,
  },
  {
    field: 'reAttemptNumber',
    hideSortIcons: true,
    headerName: 'Reattempt Number',
    flex: 0.6,
  },
  {
    field: 'actionTakenBy',
    hideSortIcons: true,
    headerName: 'Action Taken By',
    flex: 1.2,
  },
  {
    field: 'reattemptRequestDate',
    headerAlign: 'center',
    hideSortIcons: true,
    headerName: 'Reattempt Request Date',
    flex: 0.8,
    renderCell: (params) => {
      const { row } = params
      return row.nprCurrentAttemptDate ? formatDate(row?.nprCurrentAttemptDate, true) : ''
    },
  },
  {
    field: 'actionDate',
    hideSortIcons: true,
    headerName: 'Action Date',
    flex: 0.7,
    renderCell: (params) => {
      const { row } = params
      return formatDate(row?.actionDate)
    },
  },
  {
    field: 'updatedAddress',
    hideSortIcons: true,
    headerName: 'Updated Address',
    flex: 1.5,
  },
  {
    field: 'landmark',
    hideSortIcons: true,
    headerName: 'Updated Landmark',
    flex: 1,
  },
  {
    field: 'updatedContactNumber',
    hideSortIcons: true,
    headerName: 'Update Contact',
    flex: 1,
  },
]

// Module-scope so the Current Appointment card doesn't redefine a new component
// type on every View() render — a fresh component definition makes React treat
// it as a brand-new node, blowing reconciliation and any internal state.
function ApptField({ icon, label, value, span = 3 }) {
  return (
    <Grid item xs={6} md={span}>
      <Stack spacing={0.25}>
        <Stack direction='row' spacing={0.5} alignItems='center'>
          {icon}
          <Typography variant='caption' color='text.secondary' fontWeight={600} sx={{ textTransform: 'uppercase', fontSize: 10, letterSpacing: 0.4 }}>
            {label}
          </Typography>
        </Stack>
        <Typography variant='body2' sx={{ wordBreak: 'break-word', fontWeight: 500 }}>
          {value || '-'}
        </Typography>
      </Stack>
    </Grid>
  );
}

export default function View() {
  const [order, setOrder] = React.useState({})
  const [podLoading, setPodLoading] = React.useState(false)
  const [prismIedd, setPrismIedd] = React.useState(null)
  const [odaResult, setOdaResult] = React.useState(null)
  // sku_id -> Product Master images[] for the items on this order
  const [skuImageMap, setSkuImageMap] = React.useState({})
  const { id } = useParams()
  const [page, setPage] = useState(0)
  const [sizePerPage, setSizePerPage] = useState(10)
  const [ndrActionLog, setNdrActionLog] = useState(null)
  const [nprActionLog, setNPrActionLog] = useState(null)
  const [wdchangers, setWdCharges] = useState([])
  const [ndrCalls, setNdrCalls] = useState([])
  const [nprCalls, setNprCalls] = useState([])
  const [ndrQ, setNdrQ] = useState([])
  const [ndrQModal, setNdrQModal] = useState(false)
  const [sid /* setId */] = useState({})
  const [qc, setQc] = useState(false)
  const [logs, setLogs] = useState([])
  const [logsOpen, setLogsOpen] = useState(false)
  const [sorterImagesModal, setSorterImagesModal] = useState(false)
  let [searchParams] = useSearchParams()
  const { enqueueSnackbar } = useSnackbar()
  const { piiAccessAllow } = jwt_decode(getToken())
  const acesss = getAccess()
  const showAmount = acesss.includes('SHOW_AMOUNT')
  // Local reveal toggle for appointment alt contacts (not OTP-gated since these
  // are user-entered numbers, not customer PII). Default: masked.
  const [apptContactsVisible, setApptContactsVisible] = useState(false)

  const filter = {
    andfilter: [],
    offset: 0,
    limit: 1000,
    skipCount: true,
    includes: 'id,name,cpAccountCode,prozoId,serviceTypePrimary',
  }
  const { report } = useSelector((state) => state)

  const columnsNDRActionLog = [
    {
      field: 'ndrAction',
      hideSortIcons: true,
      headerName: 'Action Type',
      flex: 0.8,
    },
    {
      field: 'reattemptNumber',
      hideSortIcons: true,
      headerName: 'Reattempt Number',
      flex: 0.6,
    },
    {
      field: 'actionTakenBy',
      hideSortIcons: true,
      headerName: 'Action Taken By',
      flex: 1.2,
    },
    {
      field: 'reattemptRequestDate',
      headerAlign: 'center',
      hideSortIcons: true,
      headerName: 'Reattempt Request Date',
      flex: 0.8,
      renderCell: (params) => {
        const { row } = params
        return row.ndrCurrentAttemptDate ? formatDate(row?.ndrCurrentAttemptDate, true) : ''
      },
    },
    {
      field: 'actionDate',
      hideSortIcons: true,
      headerName: 'Action Date',
      flex: 0.7,
      renderCell: (params) => {
        const { row } = params
        return formatDate(row?.actionDate)
      },
    },
    {
      field: 'updatedAddress',
      hideSortIcons: true,
      headerName: 'Updated Address',
      flex: 1.5,
    },
    {
      field: 'landmark',
      hideSortIcons: true,
      headerName: 'Updated Landmark',
      flex: 1,
    },
    {
      field: 'updatedContactNumber',
      hideSortIcons: true,
      headerName: 'Update Contact',
      flex: 1,
    },
    {
      field: 'vocFile',
      hideSortIcons: true,
      headerName: 'BPR File',
      flex: 1,
      renderCell: (params) => {
        const { row } = params
        if (row?.vocRemark && piiAccessAllow) {
          const latestUpdate = row?.vocRemark

          if (latestUpdate?.fileUrl) {
            const fileExtension = latestUpdate.fileUrl.split('.').pop()?.toLowerCase()
            let displayText = 'View File'
            let icon = '📄'

            if (['png', 'jpg', 'jpeg', 'webp', 'gif'].includes(fileExtension)) {
              displayText = 'View Image'
              icon = '🖼️'
            } else if (['mp3', 'wav', 'm4a', 'aac'].includes(fileExtension)) {
              displayText = 'Play Audio'
              icon = '🎵'
            } else if (['mp4', 'avi', 'mov', 'mkv'].includes(fileExtension)) {
              displayText = 'Play Video'
              icon = '🎬'
            } else if (fileExtension === 'pdf') {
              displayText = 'View PDF'
              icon = '📋'
            }

            return (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '14px' }}>{icon}</span>
                <Link
                  href={latestUpdate.fileUrl}
                  target='_blank'
                  rel='noopener noreferrer'
                  style={{
                    color: '#1976d2',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    fontWeight: '500',
                    fontSize: '0.875rem',
                  }}
                >
                  {displayText}
                </Link>
              </div>
            )
          } else {
            return (
              <span
                style={{
                  color: '#666',
                  fontStyle: 'italic',
                  fontSize: '0.875rem',
                }}
              >
                No file attached
              </span>
            )
          }
        } else if (row?.vocRemark && !piiAccessAllow) {
          return (
            <span
              style={{
                color: '#d32f2f',
                fontWeight: '500',
                fontSize: '0.875rem',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              🔒 No Access
            </span>
          )
        } else {
          return (
            <span
              style={{
                color: '#999',
                fontSize: '0.875rem',
              }}
            >
              —
            </span>
          )
        }
      },
    },
  ]

  const ndrCallcolumns = [
    {
      field: 'ndrresults',
      headerAlign: 'left',
      hideSortIcons: true,
      headerName: 'NDR Results',
      flex: 1,
      renderCell: (params) => {
        const { row } = params
        return (
          <Grid container rowSpacing={2}>
            <Grid item xs={12}>
              {row.ndr_result}
            </Grid>
          </Grid>
        )
      },
    },
    {
      field: 'ndr_outcome',
      headerAlign: 'left',
      hideSortIcons: true,
      headerName: 'Outcome',
      flex: 1,
      renderCell: (params) => {
        const { row } = params
        const outcome = row?.ndr_outcome === true ? 'TRUE' : 'FALSE'
        const color = row?.ndr_outcome === true ? 'success' : 'error'
        return (
          <Grid container rowSpacing={2}>
            <Grid item xs={12}>
              <Chip size='small' color={color} label={outcome} />
            </Grid>
          </Grid>
        )
      },
    },
    {
      field: 'startTime',
      headerAlign: 'left',
      hideSortIcons: true,
      headerName: 'Call time',
      flex: 1.1,
      renderCell: (params) => {
        const { row } = params
        return formatDate(row.call_start_time)
      },
    },
    {
      field: 'callDuration',
      headerAlign: 'left',
      hideSortIcons: true,
      headerName: 'Call Duration',
      flex: 1,
      renderCell: (params) => {
        const { row } = params
        return formatCallDurationMin(row?.call_duration)
      },
    },
    {
      field: 'recordingURL',
      headerAlign: 'left',
      hideSortIcons: true,
      headerName: 'Recordings',
      flex: 1,
      renderCell: (params) => {
        const { row } = params
        return (
          <Link href={row.recording_url} target='_blank' rel='noopener noreferrer'>
            {row.recording_url ? 'Play' : '-'}
          </Link>
        )
      },
    },
    {
      field: 'failedOrders',
      align: 'center',
      headerAlign: 'center',
      hideSortIcons: true,
      headerName: 'Failed Orders',
      flex: 1,
      renderCell: (params) => {
        const { row } = params
        // console.log(row)
        if (row?.call_responses && row.call_responses.length > 0) {
          return (
            <Grid container rowSpacing={2}>
              <Grid item xs={12}>
                <Button
                  onClick={() => {
                    setNdrQ(row.call_responses)
                    setNdrQModal(true)
                  }}
                >
                  Questions asked to Customer
                </Button>
              </Grid>
            </Grid>
          )
        } else {
          return '-'
        }
      },
    },
  ]
  const nprCallcolumns = [
    {
      field: 'npr_result',
      headerAlign: 'left',
      hideSortIcons: true,
      headerName: 'NPR Results',
      flex: 1,
      renderCell: (params) => {
        const { row } = params
        return (
          <Grid container rowSpacing={2}>
            <Grid item xs={12}>
              {row.npr_result}
            </Grid>
          </Grid>
        )
      },
    },
    {
      field: 'npr_outcome',
      headerAlign: 'left',
      hideSortIcons: true,
      headerName: 'Outcome',
      flex: 1,
      renderCell: (params) => {
        const { row } = params
        const outcome = row?.npr_outcome === true ? 'TRUE' : 'FALSE'
        const color = row?.npr_outcome === true ? 'success' : 'error'
        return (
          <Grid container rowSpacing={2}>
            <Grid item xs={12}>
              <Chip size='small' color={color} label={outcome} />
            </Grid>
          </Grid>
        )
      },
    },
    {
      field: 'startTime',
      headerAlign: 'left',
      hideSortIcons: true,
      headerName: 'Call time',
      flex: 1.1,
      renderCell: (params) => {
        const { row } = params
        return formatDate(row.call_start_time)
      },
    },
    {
      field: 'callDuration',
      headerAlign: 'left',
      hideSortIcons: true,
      headerName: 'Call Duration',
      flex: 1,
      renderCell: (params) => {
        const { row } = params
        return formatCallDurationMin(row?.call_duration)
      },
    },
    {
      field: 'recordingURL',
      headerAlign: 'left',
      hideSortIcons: true,
      headerName: 'Recordings',
      flex: 1,
      renderCell: (params) => {
        const { row } = params
        return (
          <Link href={row.recording_url} target='_blank' rel='noopener noreferrer'>
            {row.recording_url ? 'Play' : '-'}
          </Link>
        )
      },
    },
    {
      field: 'failedOrders',
      align: 'center',
      headerAlign: 'center',
      hideSortIcons: true,
      headerName: 'Failed Orders',
      flex: 1,
      renderCell: (params) => {
        const { row } = params
        // console.log(row)
        if (row?.call_responses && row.call_responses.length > 0) {
          return (
            <Grid container rowSpacing={2}>
              <Grid item xs={12}>
                <Button
                  onClick={() => {
                    setNdrQ(row.call_responses)
                    setNdrQModal(true)
                  }}
                >
                  Questions asked to Customer
                </Button>
              </Grid>
            </Grid>
          )
        } else {
          return '-'
        }
      },
    },
  ]
  const { data } = useGetAllCourierPartnersCachedQuery(filter)
  const filteredData = data && data.filter((item) => item.prozoId === order.courierDetail?.prozoId)
  const [insuranceDetails, setInsuranceDetails] = useState(null)
  const fetchInsuranceDetails = async (awbNumber) => {
    const requestBody = {
      collection: 'proposals',
      andfilter: [
        {
          field: 'awbNumber',
          operator: 'eq',
          value: awbNumber,
        },
      ],
    }
    return postGeneric(apiConstants.REPORT_GENERIC_SEARCH, requestBody)
      .then((res) => {
        if (res?.data?.data?.length > 0) {
          setInsuranceDetails(res?.data?.data?.[0])
        }
      })
      .catch((err) => {
        console.log(err)
      })
  }

  const andfilter = [
    {
      field: 'awbNumber',
      operator: 'eq',
      value: id,
    },
  ]

  // Fetch Product Master catalog images for this order's SKUs and map them by
  // sku_id so the "Product" column can show thumbnails. product_master.merchantId
  // holds the merchant OID (NOT the numeric merchantId) — which is exactly
  // order.merchantDetail.key — so we scope by that to avoid same-SKU collisions
  // across merchants.
  useEffect(() => {
    const items = order?.item_list || []
    const skus = [...new Set(items.map((it) => it?.sku_id).filter(Boolean))]
    if (!skus.length) {
      setSkuImageMap({})
      return
    }
    const merchantOid = order?.merchantDetail?.key

    const requestBody = {
      collection: 'product_master',
      offset: 0,
      limit: 200,
      skipCount: true,
      countOnly: false,
      andfilter: [
        ...(merchantOid ? [{ field: 'merchantId', operator: 'eq', value: merchantOid }] : []),
        { field: 'skuCode', operator: 'in', value: skus },
      ],
    }
    postGeneric(apiConstants.REPORT_GENERIC_SEARCH, requestBody)
      .then((res) => {
        const map = {}
        for (const p of res?.data?.data || []) {
          if (p?.skuCode) map[p.skuCode] = imagesOf(p)
        }
        setSkuImageMap(map)
      })
      .catch((err) => {
        console.log('product-master image fetch failed', err)
        setSkuImageMap({})
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.id, order?.merchantDetail?.key])
  useEffect(() => {
    const exclude =
      'label_url,invoice_url,merchantPricingRTO.merchantRateCard,merchantPricingRTO.pricingObj,courierPricingRTO.pricingObj,courierPricingRTO.rateCard,pickupDetails.pickupTime,actualDeliveryTat,invoiceNumber,shippingCharge,transactionCharge,giftwrapCharge,dispatchMode,proshipDeliveryTat,courierDeliveryTat,itemList.hsn,itemList.itemDescription,order_history.courierPartnerId,order_history.courierPartnerEdd,merchant,courier,version,orderManifest,overriddenCourierAccountCode,isDiscrepancyRaised,discrepancyId,securityKey,sortCode,channelType,createdByUser,skipRule,modifiedByUser,rvp_reason,customerDetail,cpErrorList,mongoLabelSignedUrlDetails,overriddenCourierRule,mongoInvoiceSignedUrlDetails,merchant,merchantPricing.merchantRateCard,merchantPricing.pricingObj,courierPricing.rateCard,courierPricing.pricingObj,modifiedByUser,lastModifiedDate'
    let d = { andfilter: andfilter, excludes: exclude }
    post(apiConstants.ORDER_SEARCH, d).then((res) => {
      let data = res.data
      let order = data?.data[0]
      if (data?.count > 1) {
        let id = searchParams.get('id')
        if (!id) {
          enqueueSnackbar(
            'Duplicate AWB Numbers, id handling is needed - pls raise to tech team.',
            { variant: 'error' }
          )
        }
        let allOrders = data?.data
        order = allOrders.filter((val) => val.id === id)[0]
      }
      let orderId = order.id
      setOrder(order)
      let naLog = order.ndrData?.actionUpdateLog || []
      let nprLog = order.nprData?.actionUpdateLog || []
      setNdrActionLog(naLog)
      setNPrActionLog(nprLog)
      if ((order?.shipment_type === 'B2B' || order?.shipmentType === 'B2B') && orderId) {
        getGeneric(apiConstants.B2B_ODA_CHECK, { orderId })
          .then((odaRes) => {
            const hit = (odaRes?.data?.responseObj?.results || []).find((r) => r?.oda)
            setOdaResult(hit || null)
          })
          .catch((err) => {
            console.error('B2B ODA check failed for orderId=' + orderId, err)
            setOdaResult(null)
          })
      } else {
        setOdaResult(null)
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])


  const handleClose = () => {
    setQc(false)
    setLogsOpen(false)
    setLogs([])
  }

  /* const openDocument = async (url, type) => {
    const newUrl = await getS3SignedUrl(
      url,
      {
        awbNumber: order.awb_number,
        docType: type,
      },
      type
    )

    window.open(newUrl, true)
  } */

  const payment_mode = order && order.payment_mode ? order.payment_mode : ''

  useEffect(() => {
    if (order?.isWDSetteled === true) {
      post(apiConstants.WEIGHT_DISCREPANCY, { awbNumber: order.awb_number })
        .then((res) => {
          setWdCharges(res?.data?.responseObj[0])
        })
        .catch((err) => console.log(err))
    }
  }, [order])

  // PRISM iEDD (shadow, read-only) — silent-fail: absent/unreachable PRISM
  // leaves the page exactly as before
  useEffect(() => {
    if (!order?.awb_number) return
    getGeneric(`prism/iedd/${encodeURIComponent(order.awb_number)}`)
      .then((res) => setPrismIedd(res.data))
      .catch(() => setPrismIedd(null))
  }, [order?.awb_number])

  useEffect(() => {
    if (!order?.awb_number) return

    const payload = {
      // collection: order?.nprData ? 'futworks_npr_recording' : 'futworks_ndr_recording',
      limit: 100,
      skipCount: true,
      countOnly: false,
      andfilter: [
        {
          field: 'awb_number',
          operator: 'eq',
          value: id,
        },
      ],
    }
        if (order?.nprData) {
      postGeneric(apiConstants.NPR_RECORDING_SEARCH, payload)
        .then((res) => {
          setNprCalls(res?.data?.data)
        })
        .catch((err) => {
          console.log(err)
        })
    } else {
      postGeneric(apiConstants.NDR_RECORDING_SEARCH, payload)
        .then((res) => {
          setNdrCalls(res?.data?.data)
        })
        .catch((err) => {
          console.log(err)
        })
    }
    
    fetchInsuranceDetails(order?.awb_number)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.awb_number])

  const handleOpen1 = (data) => {
    setLogsOpen(true)
    setLogs(data)
  }

  const isReverseShipment = order?.order_type === 'REVERSE SHIPMENT'

  const columnsFutworkDate = [
    {
      field: 'Sno',
      hideSortIcons: true,
      headerName: 'SNo.',
      flex: 0.5,
      renderCell: (params) => {
        const { row } = params
        return row.id + 1
      },
    },
    {
      field: 'currentAttemptCount',
      hideSortIcons: true,
      headerName: 'Current Attempt Count',
      flex: 0.5,
      renderCell: (params) => {
        const { row } = params
        return row?.currentAttemptCount
      },
    },
    {
      field: 'leadId',
      hideSortIcons: true,
      headerName: 'Lead ID',
      flex: 1,
      renderCell: (params) => {
        const { row } = params
        return row?.leadId
      },
    },
    {
      field: 'updateHistories',
      hideSortIcons: true,
      headerName: 'Update History',
      flex: 0.5,
      renderCell: (params) => {
        const { row } = params
        return (
          <>
            <Tooltip title='View'>
              <IconButton
                color='primary'
                size='small'
                onClick={() => handleOpen1(row?.updateHistories)}
              >
                <VisibilityIcon fontSize='small' />
              </IconButton>
            </Tooltip>
          </>
        )
      },
    },
  ]
  const columns2 = [
    {
      field: 'attemptCount',
      hideSortIcons: true,
      headerName: 'Attempt Count',
      flex: 1,
      renderCell: (params) => {
        const { row } = params
        return row?.attemptCount
      },
    },
    {
      field: 'time',
      hideSortIcons: true,
      headerName: 'Time',
      flex: 1,
      renderCell: (params) => {
        const { row } = params
        return formatDate(row?.time)
      },
    },
    {
      field: 'type',
      hideSortIcons: true,
      headerName: 'Type',
      flex: 1,
      renderCell: (params) => {
        const { row } = params
        return row?.type
      },
    },
  ]

  // const handleView = async (url) => {
  //     const refreshedUrl = await refreshS3SignedUrl(url);
  //     window.open(refreshedUrl, '_blank');
  // };

  const [otpCancellationDetails, setOtpCancellationDetails] = useState(null)
  const [otpCancellationModal, setOtpCancellationModal] = useState(false)
  const [loadingOtpDetails, setLoadingOtpDetails] = useState(false)

  const getOtpCancellationDetails = async () => {
    try {
      setLoadingOtpDetails(true)
      const payload = {
        merchantOid: order.merchantDetail?.key,
        awbNumbersList: [order.awb_number],
      }
      const res = await postGeneric(apiConstants.GET_OTP_CANCELLATION_DETAILS, payload)
      if (res?.status === 200) {
        setOtpCancellationDetails(res?.data)
      }
    } catch (error) {
      console.error('Failed to fetch OTP cancellation details:', error)
      enqueueSnackbar('Failed to fetch OTP cancellation details', { variant: 'error' })
    } finally {
      setLoadingOtpDetails(false)
    }
  }

  const handleOtpCancellationClick = async () => {
    setOtpCancellationModal(true)
    if (!otpCancellationDetails) {
      await getOtpCancellationDetails()
    }
  }

  return (
    <Page
      title=''
      showBack
      actions={
        <ButtonGroup disableElevation variant='contained' color='warning'>
          <MUIModal
            open={logsOpen}
            handleClose={handleClose}
            maxWidth='md'
            children={
              <ProzoDataGrid
                columns={columns2}
                autoHeight={true}
                rows={logs?.length > 0 ? logs?.map((val, index) => ({ ...val, id: index })) : []}
                pagination={false}
                hideFooterPagination={true}
                paginationMode={'client'}
                rowHeight={60}
                rowCount={logs?.length || 0}
                getRowId={(row) => row.id}
              />
            }
            title={`Details`}
          />
          <MUIModal
            open={qc}
            handleClose={handleClose}
            children={<QcDetails data={sid} />}
            title={'Qc Link'}
            maxWidth='md'
          />
          <PageTitle>Order Details </PageTitle>
          {/* {order?.invoice_url &&
                    <Button onClick={() => openDocument(order?.invoice_url, 'INVOICE')} startIcon={<ReceiptIcon />}>
                        Invoice
                    </Button>
                }
                {!isReverseShipment &&
                    <Button disabled={order?.shipment_type === 'B2B' && order?.isTrackOnlyOrder} onClick={() => openDocument(order?.label_url, 'LABEL')} startIcon={<DescriptionIcon />}>
                        Label
                    </Button>
                } */}
          {podAvailable(order) && (
            <Button
              color="primary"
              disabled={podLoading}
              onClick={() => {
                setPodLoading(true)
                downloadPOD(order)
                  .catch((e) => console.error('[POD]', e))
                  .finally(() => setPodLoading(false))
              }}
              startIcon={podLoading ? <CircularProgress size={16} color="inherit" /> : <PictureAsPdfIcon />}
              sx={{ bgcolor: '#2065D1', color: '#fff', '&:hover': { bgcolor: '#103996' }, '&.Mui-disabled': { bgcolor: '#2065D1', color: '#fff', opacity: 0.7 } }}
            >
              {podLoading ? 'Generating…' : 'Download POD'}
            </Button>
          )}
        </ButtonGroup>
      }
    >
      <Box sx={detailSkinSx}>
      <StatusBanner
        order={order}
        courierAccountCode={filteredData && filteredData.length > 0 ? filteredData[0].cpAccountCode : null}
        serviceType={filteredData && filteredData.length > 0 ? filteredData[0].serviceTypePrimary : null}
        paymentMode={payment_mode}
      />

      <SummaryCards order={order} />

      <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={6}>
                  <KitSectionCard title='Identifiers' sx={{ mb: 0, height: '100%' }}>
                  <KitInfoRow label='Order Number' value={order?.orderId} />
                  <KitInfoRow label='Shipping Label ID' value={order?.shippingLabelId} />
                  <KitInfoRow label='RTO AWB Number' value={
                    order?.rto_awb
                      ? <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '2px' }}>{order.rto_awb}<CopyIcon text={order.rto_awb} /></Box>
                      : null
                  } />
                  {order?.channelOrderName && <KitInfoRow label='Channel Order Name' value={order.channelOrderName} />}
                  <KitInfoRow label='Eway Bill Number' value={order?.ewaybill} />
                  <KitInfoRow label='Proship Tracking ID' value={
                    order?.createTrackingId
                      ? <Link component='a' href={`/shipping-label?trackingId=${order?.createTrackingId}`} sx={{ fontSize: 12, wordBreak: 'break-all', color: '#2065D1', '&:hover': { color: '#103996' } }}>{order.createTrackingId}</Link>
                      : null
                  } />
                  <KitInfoRow label='ClickPost Logs' value={
                    <Link href={`/carrier-logs?reference=${order?.reference}`} target='_blank' rel='noopener noreferrer' underline='hover' sx={{ fontSize: 12, color: '#2065D1', '&:hover': { color: '#103996' } }}>View Logs</Link>
                  } />
                  </KitSectionCard>
                </Grid>

                <Grid item xs={12} md={6}>
                  <KitSectionCard title='Status & QC' sx={{ mb: 0, height: '100%' }}>
                  {order?.orderStatus === 'RTO_DELIVERED' && (
                    <KitInfoRow label='RTO Delivered Date' value={parseDateAndTime(order?.terminalStatus?.actualTime)} />
                  )}
                  <KitInfoRow label='Invoice Generated' value={order?.invoiceKey ? 'Yes' : 'No'} />
                  {order?.paymentModeChange?.length > 0 && (
                    <KitInfoRow label='Payment Changes' value={
                      <Box>
                        {order.paymentModeChange.map((change, i) => {
                          const meta = [
                            change?.changedBy ? `by ${change.changedBy}` : null,
                            change?.changedAt ? formatDate(change.changedAt) : null,
                          ].filter(Boolean).join(' · ')
                          return (
                            <Box key={i} sx={{ mt: i ? 0.5 : 0 }}>
                              <Typography sx={{ fontSize: 11.5, color: '#B72136', fontWeight: 600 }}>
                                {`${change?.existingPaymentMode} → ${change?.newPaymentMode}`}
                              </Typography>
                              {meta && (
                                <Typography sx={{ fontSize: 10.5, color: 'text.secondary' }}>
                                  {meta}
                                </Typography>
                              )}
                            </Box>
                          )
                        })}
                      </Box>
                    } />
                  )}
                  <KitInfoRow label='OTP Verified Delivery' value={
                    <Chip size='small' color={order?.otpVerifiedDelivery ? 'success' : 'default'} label={order?.otpVerifiedDelivery ? 'Yes' : 'No'} />
                  } />
                  <KitInfoRow label='OTP Verified Cancellation' value={
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                      <Chip size='small' color={order?.otpVerifiedCancellation ? 'error' : 'default'} label={order?.otpVerifiedCancellation ? 'Yes' : 'No'} />
                      {order?.otpVerifiedCancellation && (
                        <Link component='button' onClick={handleOtpCancellationClick} sx={{ fontSize: 11.5, color: '#2065D1', '&:hover': { color: '#103996' } }}>View Details</Link>
                      )}
                    </Box>
                  } />
                  {order?.sorterImages?.length > 0 && (
                    <KitInfoRow label='Sorter Images' value={<Link component='button' onClick={() => setSorterImagesModal(true)} sx={{ fontSize: 12, color: '#2065D1', '&:hover': { color: '#103996' } }}>View Images</Link>} />
                  )}
                  {order?.shipment_type === 'B2B' && order.label_url && order.label_url !== '' && (
                    <KitInfoRow label='LR Copy' value={<Link href={order?.label_url} target='_blank' rel='noopener noreferrer' sx={{ fontSize: 12, color: '#2065D1', '&:hover': { color: '#103996' } }}>LR Copy</Link>} />
                  )}
                  {order?.shipment_type === 'B2B' && order?.isTrackOnlyOrder && (
                    <Typography variant='caption' color='error' sx={{ fontStyle: 'italic', display: 'block', mt: 0.5 }}>
                      Track-only order — shipping label not available.
                    </Typography>
                  )}
                  {isReverseShipment && (
                    <>
                      <KitInfoRow label='QC Verified at Pickup' value={<Chip size='small' label={order?.qc_type ? 'Yes' : 'No'} color={order?.qc_type ? 'success' : 'default'} />} />
                      <KitInfoRow label='QC Remarks' value={(() => {
                        if (!order?.order_history) return '—'
                        const pickupFailed = order.order_history.find((item) => item?.orderStatusEnum === 'PICKUP_FAILED')
                        return pickupFailed?.remark || '—'
                      })()} />
                      {order?.qcDetails && (
                        <KitInfoRow label='QC Images' value={
                          <Stack direction='row' spacing={1} flexWrap='wrap' justifyContent='flex-end'>
                            {order?.qcDetails?.images?.length > 0 ? (
                              order.qcDetails.images.map((imgObj, idx) => {
                                const imgUrl = typeof imgObj === 'string' ? imgObj : imgObj?.signedLink
                                return (
                                  <a key={idx} href={imgUrl} target='_blank' rel='noopener noreferrer'>
                                    <img src={imgUrl} alt={`QC ${idx + 1}`} style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 4, border: '1px solid #eee' }} />
                                  </a>
                                )
                              })
                            ) : (
                              <Typography variant='body2' sx={{ color: 'text.secondary' }}>No Images</Typography>
                            )}
                          </Stack>
                        } />
                      )}
                    </>
                  )}
                  </KitSectionCard>
                </Grid>

                <Grid item xs={12} md={6}>
                  <KitSectionCard title='Courier & Timing' sx={{ mb: 0, height: '100%' }}>
                  <KitInfoRow label='Proship Account Code' value={order?.courierDetail?.prozoId} />
                  <KitInfoRow label='CP Account Code' value={filteredData && filteredData.length > 0 ? filteredData[0].cpAccountCode : null} />
                  <KitInfoRow label='Last Status Update' value={order?.lastStatusUpdateTime ? parseDateAndTime(order.lastStatusUpdateTime) : null} />
                  {order?.customEdd && <KitInfoRow label='Custom EDD' value={formatDate(order.customEdd, true)} />}
                  {prismIedd?.iedd_date && (
                    <KitInfoRow label='PRISM iEDD (shadow)' value={
                      <Tooltip title={`attribution: ${prismIedd.attribution_level || '—'} · last update: ${prismIedd.update_reason || '—'}${prismIedd.iedd_range_end ? ` · range end ${prismIedd.iedd_range_end}` : ''}`}>
                        <span>
                          {prismIedd.iedd_date}
                          {prismIedd.confidence_tier && (
                            <Chip size='small' sx={{ ml: 0.5 }} label={prismIedd.confidence_tier} color={prismIedd.confidence_tier === 'HIGH' ? 'success' : prismIedd.confidence_tier === 'MEDIUM' ? 'warning' : 'default'} />
                          )}
                        </span>
                      </Tooltip>
                    } />
                  )}
                  </KitSectionCard>
                </Grid>

                <Grid item xs={12} md={6}>
                  <KitSectionCard title='Pricing & COD' sx={{ mb: 0, height: '100%' }}>
                  {showAmount && (
                    <>
                      <KitInfoRow label='Courier Price' value={order?.courier_pricing?.price ? formatCurrency(order.courier_pricing.price) : null} />
                      <KitInfoRow label='Courier Price RTO' value={order?.courier_pricing_rto?.price ? formatCurrency(order.courier_pricing_rto.price) : null} />
                    </>
                  )}
                  <KitInfoRow label='COD File Generated' value={<Chip size='small' color={order?.codFileRef ? 'success' : 'error'} label={order?.codFileRef ? 'Yes' : 'No'} />} />
                  <KitInfoRow label='Merchant COD Remittance' value={<Chip size='small' color={order?.merchantRemittanceStatus ? 'success' : 'error'} label={order?.merchantRemittanceStatus ? 'Remitted' : 'Pending'} />} />
                  <KitInfoRow label='Courier COD Remittance' value={<Chip size='small' color={order?.courierRemittanceStatus ? 'success' : 'error'} label={order?.courierRemittanceStatus ? 'Received' : 'Pending'} />} />
                  {insuranceDetails && (
                    <KitInfoRow label='Insurance' value={
                      insuranceDetails?.policyCertificateLink
                        ? <Link href={insuranceDetails.policyCertificateLink} target='_blank' rel='noopener noreferrer' sx={{ fontSize: 12, color: '#2065D1', '&:hover': { color: '#103996' } }}>{insuranceDetails?.proposalStatus || 'View Certificate'}</Link>
                        : (insuranceDetails?.proposalStatus || 'N/A')
                    } />
                  )}
                  </KitSectionCard>
                </Grid>

                {order?.additionalData?.EDD_Calculation?.length > 0 && (
                  <Grid item xs={12} sx={{ mt: 1.5 }}>
                    <Accordion
                      elevation={0}
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: '8px !important',
                        '&::before': { display: 'none' },
                        '&.Mui-expanded': { margin: 0 },
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{
                          minHeight: 44,
                          '& .MuiAccordionSummary-content': {
                            my: 1,
                            alignItems: 'center',
                            gap: 1,
                          },
                        }}
                      >
                        <Typography variant='subtitle2' sx={{ fontWeight: 600, fontSize: 13 }}>
                          EDD Calculation
                        </Typography>
                        <Chip
                          label={`${order.additionalData.EDD_Calculation.length} steps`}
                          size='small'
                          sx={{ height: 18, fontSize: 10 }}
                        />
                      </AccordionSummary>
                      <AccordionDetails sx={{ pt: 0 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                          {order.additionalData.EDD_Calculation.map((step, index) => {
                            const isFinal = step.toLowerCase().startsWith('final')
                            return (
                              <Box
                                key={index}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'flex-start',
                                  gap: 1.5,
                                  p: 1,
                                  borderRadius: 1,
                                  backgroundColor: isFinal ? '#e8f5e9' : '#f5f5f5',
                                  borderLeft: `3px solid ${isFinal ? '#2e7d32' : '#1976d2'}`,
                                }}
                              >
                                <Chip
                                  label={index + 1}
                                  size='small'
                                  sx={{
                                    minWidth: 28,
                                    height: 22,
                                    fontSize: '0.7rem',
                                    backgroundColor: isFinal ? '#2e7d32' : '#1976d2',
                                    color: '#fff',
                                    flexShrink: 0,
                                  }}
                                />
                                <Typography
                                  variant='caption'
                                  sx={{
                                    lineHeight: 1.6,
                                    color: isFinal ? '#2e7d32' : 'text.primary',
                                    fontWeight: isFinal ? 600 : 400,
                                  }}
                                >
                                  {step}
                                </Typography>
                              </Box>
                            )
                          })}
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  </Grid>
                )}
      </Grid>
        {order?.isWDSetteled === true && (
          <Grid paddingBottom={2}>
            <Card>
              <CardHeader title='Weight Discrepancy' sx={{ fontWeight: 800 }} />
              <CardContent>
                <Grid container spacing={1}>
                  <Grid item xs={6} md={3}>
                    <Typography variant='subtitle2' color={'black'}>
                      Weight Discrepancy Status{' '}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant='subtitle2'>
                      {order && order?.wdStatus ? order?.wdStatus : ''}{' '}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant='subtitle2' color={'black'}>
                      Update Courier Price{' '}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant='subtitle2'>
                      {order && order?.wdChargedPriceCourier
                        ? formatCurrency(order?.wdChargedPriceCourier)
                        : ''}{' '}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant='subtitle2' color={'black'}>
                      Update Merchant Price{' '}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant='subtitle2'>
                      {order && order?.wdChargedPriceMerchant
                        ? formatCurrency(order?.wdChargedPriceMerchant)
                        : ''}{' '}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant='subtitle2' color={'black'}>
                      Updated Weight{' '}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant='subtitle2'>
                      {order && order?.wdChargedWeight ? order?.wdChargedWeight / 1000 + ' kg' : ''}{' '}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant='subtitle2' color={'black'}>
                      Merchant Original Weight Slab{' '}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant='subtitle2'>
                      {wdchangers?.weightSlabsMerchant
                        ? (
                          wdchangers?.weightSlabsMerchant?.originalSlab?.startWeight / 1000
                        ).toFixed(2)
                        : 'NA'}{' '}
                      -{' '}
                      {wdchangers?.weightSlabsMerchant
                        ? (wdchangers?.weightSlabsMerchant?.originalSlab?.endWeight / 1000).toFixed(
                          2
                        )
                        : 'NA'}{' '}
                      kg
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant='subtitle2' color={'black'}>
                      Merchant Applied Weight Slab{' '}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant='subtitle2'>
                      {wdchangers?.weightSlabsMerchant
                        ? (
                          wdchangers?.weightSlabsMerchant?.finalAppliedSlab?.startWeight / 1000
                        ).toFixed(2)
                        : 'NA'}{' '}
                      -{' '}
                      {wdchangers?.weightSlabsMerchant
                        ? (
                          wdchangers?.weightSlabsMerchant?.finalAppliedSlab?.endWeight / 1000
                        ).toFixed(2)
                        : 'NA'}{' '}
                      kg{' '}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant='subtitle2' color={'black'}>
                      Courier Original Weight Slab{' '}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant='subtitle2'>
                      {wdchangers?.weightSlabsCourier
                        ? (
                          wdchangers?.weightSlabsCourier?.originalSlab?.startWeight / 1000
                        ).toFixed(2)
                        : 'NA'}{' '}
                      -{' '}
                      {wdchangers?.weightSlabsCourier
                        ? (wdchangers?.weightSlabsCourier?.originalSlab?.endWeight / 1000).toFixed(
                          2
                        )
                        : 'NA'}{' '}
                      kg{' '}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant='subtitle2' color={'black'}>
                      Courier Applied Weight Slab{' '}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant='subtitle2'>
                      {wdchangers?.weightSlabsCourier
                        ? (
                          wdchangers?.weightSlabsCourier?.finalAppliedSlab?.startWeight / 1000
                        ).toFixed(2)
                        : 'NA'}{' '}
                      -{' '}
                      {wdchangers?.weightSlabsCourier
                        ? (
                          wdchangers?.weightSlabsCourier?.finalAppliedSlab?.endWeight / 1000
                        ).toFixed(2)
                        : 'NA'}{' '}
                      kg{' '}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}
        {order && order?.appointmentDelivery && order.appointmentDeliveryData && (() => {
          const ad = order.appointmentDeliveryData;
          const altC1 = ad?.appointment_del_alt_contact_1;
          const altC2 = ad?.appointment_del_alt_contact_2;
          // Alt contacts behind a local toggle so they're not exposed by default —
          // mirrors the gated reveal pattern used on Customer Details (without OTP
          // since these are user-entered alt contacts, not customer PII).
          const renderAlt = (v) => (v
            ? (apptContactsVisible ? v : maskPhoneNumber(v))
            : '-');
          const docs = [
            { keyField: 'appointment_del_invoice_s3_key', label: 'Invoice', docType: 'INVOICE', Icon: ReceiptLongIcon, color: '#1565c0', bg: '#e3f2fd' },
            { keyField: 'appointment_del_po_copy_s3_key', label: 'PO Copy', docType: 'PO_COPY', Icon: DescriptionIcon, color: '#2e7d32', bg: '#e8f5e9' },
            { keyField: 'appointment_del_eway_bill_s3_key', label: 'E-Way Bill', docType: 'EWAY_BILL', Icon: LocalShippingIcon, color: '#e65100', bg: '#fff3e0' },
          ];
          const openDoc = async (docType) => {
            try {
              const res = await getGeneric(apiConstants.APPOINTMENT_DELIVERY_DOC_URL, {
                awbNumber: order?.awb_number, docType,
              });
              const url = res?.data?.responseObj?.url || res?.data?.url;
              if (url) window.open(url, '_blank');
              else enqueueSnackbar('Document URL missing', { variant: 'warning' });
            } catch (e) {
              enqueueSnackbar(e?.response?.data?.message || 'Failed to fetch document', { variant: 'error' });
            }
          };
          const openDocById = async (docId) => {
            try {
              const res = await getGeneric(apiConstants.APPOINTMENT_DELIVERY_DOC_URL, {
                awbNumber: order?.awb_number, docId,
              });
              const url = res?.data?.responseObj?.url || res?.data?.url;
              if (url) window.open(url, '_blank');
              else enqueueSnackbar('Document URL missing', { variant: 'warning' });
            } catch (e) {
              enqueueSnackbar(e?.response?.data?.message || 'Failed to fetch document', { variant: 'error' });
            }
          };
          return (
            <Grid paddingBottom={2}>
              <Card sx={{ borderLeft: '3px solid #1565c0' }}>
                <CardHeader
                  title={
                    <Stack direction='row' spacing={1} alignItems='center'>
                      <Typography variant='subtitle1' fontWeight={700}>Current Appointment</Typography>
                      <Chip
                        size='small'
                        color='primary'
                        label={`Attempt #${(order?.appointmentDeliveryHistory?.length || 0) + 1}`}
                        sx={{ height: 20, fontSize: 11 }}
                      />
                    </Stack>
                  }
                  action={(altC1 || altC2) ? (
                    <Button
                      size='small'
                      variant={apptContactsVisible ? 'outlined' : 'contained'}
                      color={apptContactsVisible ? 'inherit' : 'error'}
                      startIcon={apptContactsVisible ? <VisibilityOffTwoTone /> : <VisibilityTwoTone />}
                      onClick={() => setApptContactsVisible((v) => !v)}
                      sx={{ textTransform: 'none' }}
                    >
                      {apptContactsVisible ? 'Hide Contacts' : 'Show Contacts'}
                    </Button>
                  ) : null}
                  sx={{ pb: 1 }}
                />
                <CardContent sx={{ pt: 0 }}>
                  <Grid container spacing={2}>
                    <ApptField
                      icon={<EventIcon sx={{ fontSize: 14, color: '#1565c0' }} />}
                      label='Delivery Date'
                      value={order.appointment_del_date ? formatDate1(order.appointment_del_date, 'dd MMM, yyyy') : null}
                    />
                    <ApptField
                      icon={<AccessTimeIcon sx={{ fontSize: 14, color: '#1565c0' }} />}
                      label='Time Slot'
                      value={ad?.appointment_del_start
                        ? `${parseTime(ad.appointment_del_start)} – ${parseTime(ad.appointment_del_end)}`
                        : null}
                    />
                    <ApptField
                      icon={<DescriptionIcon sx={{ fontSize: 14, color: '#2e7d32' }} />}
                      label='PO #'
                      value={ad?.appointment_del_po}
                    />
                    <ApptField
                      icon={<EventIcon sx={{ fontSize: 14, color: '#2e7d32' }} />}
                      label='PO Expiry'
                      value={ad?.appointment_del_po_expiry ? formatDate1(ad.appointment_del_po_expiry, 'dd MMM, yyyy') : null}
                    />
                    <ApptField
                      icon={<PhoneIcon sx={{ fontSize: 14, color: '#5e35b1' }} />}
                      label='Alt Contact 1'
                      value={renderAlt(altC1)}
                    />
                    <ApptField
                      icon={<PhoneIcon sx={{ fontSize: 14, color: '#5e35b1' }} />}
                      label='Alt Contact 2'
                      value={renderAlt(altC2)}
                    />
                    <ApptField label='Remarks' value={ad?.appointment_del_remarks} span={6} />
                  </Grid>

                  <Divider sx={{ my: 2 }} />

                  <Stack direction='row' alignItems='center' spacing={1.5} flexWrap='wrap'>
                    <Typography variant='caption' color='text.secondary' fontWeight={600} sx={{ textTransform: 'uppercase', fontSize: 10, letterSpacing: 0.4 }}>
                      Documents
                    </Typography>
                    {docs.map(({ keyField, label, docType, Icon, color, bg }) => {
                      const has = !!ad?.[keyField];
                      return (
                        <Tooltip key={docType} title={has ? `View ${label}` : `${label} not uploaded`}>
                          <span>
                            <Button
                              size='small'
                              variant='outlined'
                              disabled={!has}
                              startIcon={<Icon sx={{ fontSize: 14 }} />}
                              onClick={() => openDoc(docType)}
                              sx={{
                                py: 0.25, px: 1, fontSize: 11, textTransform: 'none',
                                color: has ? color : 'text.disabled',
                                borderColor: has ? color : 'divider',
                                bgcolor: has ? bg : 'transparent',
                                '&:hover': has ? { bgcolor: bg, borderColor: color, filter: 'brightness(0.95)' } : {},
                              }}
                            >{label}</Button>
                          </span>
                        </Tooltip>
                      );
                    })}
                    {(ad?.appointment_del_extra_docs || ad?.extraDocs || []).map((xd) => (
                      <Tooltip key={xd.docId} title={`View ${xd.docLabel || categoryLabel(xd.docType)}`}>
                        <span>
                          <Button
                            size='small'
                            variant='outlined'
                            startIcon={<DescriptionIcon sx={{ fontSize: 14 }} />}
                            onClick={() => openDocById(xd.docId)}
                            sx={{
                              py: 0.25, px: 1, fontSize: 11, textTransform: 'none',
                              color: '#5e35b1', borderColor: '#5e35b1', bgcolor: '#ede7f6',
                              '&:hover': { bgcolor: '#ede7f6', borderColor: '#5e35b1', filter: 'brightness(0.95)' },
                            }}
                          >{xd.docLabel || categoryLabel(xd.docType)}</Button>
                        </span>
                      </Tooltip>
                    ))}
                  </Stack>

                  {(ad?.modifiedByUser || ad?.lastModifiedDate) && (
                    <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mt: 1.5, fontSize: 11 }}>
                      Last updated
                      {ad?.modifiedByUser ? ` by ${ad.modifiedByUser}` : ''}
                      {ad?.lastModifiedDate ? ` · ${formatDate1(ad.lastModifiedDate, 'dd MMM, yyyy HH:mm')}` : ''}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })()}
        {order && order?.additionalDocuments && (() => {
          const ad = order.additionalDocuments;
          const altC1 = ad?.altContact1;
          const altC2 = ad?.altContact2;
          const renderAlt = (v) => (v
            ? (apptContactsVisible ? v : maskPhoneNumber(v))
            : '-');
          const docs = [
            { keyField: 'invoiceS3Key', label: 'Invoice', docType: 'INVOICE', Icon: ReceiptLongIcon, color: '#1565c0', bg: '#e3f2fd' },
            { keyField: 'poCopyS3Key', label: 'PO Copy', docType: 'PO_COPY', Icon: DescriptionIcon, color: '#2e7d32', bg: '#e8f5e9' },
            { keyField: 'ewayBillS3Key', label: 'E-Way Bill', docType: 'EWAY_BILL', Icon: LocalShippingIcon, color: '#e65100', bg: '#fff3e0' },
          ];
          const openDoc = async (docType) => {
            try {
              const res = await getGeneric(apiConstants.ADDITIONAL_DOCUMENTS_DOC_URL, {
                awbNumber: order?.awb_number, docType,
              });
              const url = res?.data?.responseObj?.url || res?.data?.url;
              if (url) window.open(url, '_blank');
              else enqueueSnackbar('Document URL missing', { variant: 'warning' });
            } catch (e) {
              enqueueSnackbar(e?.response?.data?.message || 'Failed to fetch document', { variant: 'error' });
            }
          };
          const openDocById = async (docId) => {
            try {
              const res = await getGeneric(apiConstants.ADDITIONAL_DOCUMENTS_DOC_URL, {
                awbNumber: order?.awb_number, docId,
              });
              const url = res?.data?.responseObj?.url || res?.data?.url;
              if (url) window.open(url, '_blank');
              else enqueueSnackbar('Document URL missing', { variant: 'warning' });
            } catch (e) {
              enqueueSnackbar(e?.response?.data?.message || 'Failed to fetch document', { variant: 'error' });
            }
          };
          return (
            <Grid paddingBottom={2}>
              <Card sx={{ borderLeft: '3px solid #1565c0' }}>
                <CardHeader
                  title={
                    <Stack direction='row' spacing={1} alignItems='center'>
                      <Typography variant='subtitle1' fontWeight={700}>Additional Documents</Typography>
                    </Stack>
                  }
                  action={(altC1 || altC2) ? (
                    <Button
                      size='small'
                      variant={apptContactsVisible ? 'outlined' : 'contained'}
                      color={apptContactsVisible ? 'inherit' : 'error'}
                      startIcon={apptContactsVisible ? <VisibilityOffTwoTone /> : <VisibilityTwoTone />}
                      onClick={() => setApptContactsVisible((v) => !v)}
                      sx={{ textTransform: 'none' }}
                    >
                      {apptContactsVisible ? 'Hide Contacts' : 'Show Contacts'}
                    </Button>
                  ) : null}
                  sx={{ pb: 1 }}
                />
                <CardContent sx={{ pt: 0 }}>
                  <Grid container spacing={2}>
                    <ApptField
                      icon={<DescriptionIcon sx={{ fontSize: 14, color: '#2e7d32' }} />}
                      label='PO #'
                      value={ad?.poNumber}
                    />
                    <ApptField
                      icon={<EventIcon sx={{ fontSize: 14, color: '#2e7d32' }} />}
                      label='PO Expiry'
                      value={ad?.poExpiry ? formatDate1(ad.poExpiry, 'dd MMM, yyyy') : null}
                    />
                    <ApptField
                      icon={<PhoneIcon sx={{ fontSize: 14, color: '#5e35b1' }} />}
                      label='Alt Contact 1'
                      value={renderAlt(altC1)}
                    />
                    <ApptField
                      icon={<PhoneIcon sx={{ fontSize: 14, color: '#5e35b1' }} />}
                      label='Alt Contact 2'
                      value={renderAlt(altC2)}
                    />
                  </Grid>

                  <Divider sx={{ my: 2 }} />

                  <Stack direction='row' alignItems='center' spacing={1.5} flexWrap='wrap'>
                    <Typography variant='caption' color='text.secondary' fontWeight={600} sx={{ textTransform: 'uppercase', fontSize: 10, letterSpacing: 0.4 }}>
                      Documents
                    </Typography>
                    {docs.map(({ keyField, label, docType, Icon, color, bg }) => {
                      const has = !!ad?.[keyField];
                      return (
                        <Tooltip key={docType} title={has ? `View ${label}` : `${label} not uploaded`}>
                          <span>
                            <Button
                              size='small'
                              variant='outlined'
                              disabled={!has}
                              startIcon={<Icon sx={{ fontSize: 14 }} />}
                              onClick={() => openDoc(docType)}
                              sx={{
                                py: 0.25, px: 1, fontSize: 11, textTransform: 'none',
                                color: has ? color : 'text.disabled',
                                borderColor: has ? color : 'divider',
                                bgcolor: has ? bg : 'transparent',
                                '&:hover': has ? { bgcolor: bg, borderColor: color, filter: 'brightness(0.95)' } : {},
                              }}
                            >{label}</Button>
                          </span>
                        </Tooltip>
                      );
                    })}
                    {(ad?.appointment_del_extra_docs || ad?.extraDocs || []).map((xd) => (
                      <Tooltip key={xd.docId} title={`View ${xd.docLabel || categoryLabel(xd.docType)}`}>
                        <span>
                          <Button
                            size='small'
                            variant='outlined'
                            startIcon={<DescriptionIcon sx={{ fontSize: 14 }} />}
                            onClick={() => openDocById(xd.docId)}
                            sx={{
                              py: 0.25, px: 1, fontSize: 11, textTransform: 'none',
                              color: '#5e35b1', borderColor: '#5e35b1', bgcolor: '#ede7f6',
                              '&:hover': { bgcolor: '#ede7f6', borderColor: '#5e35b1', filter: 'brightness(0.95)' },
                            }}
                          >{xd.docLabel || categoryLabel(xd.docType)}</Button>
                        </span>
                      </Tooltip>
                    ))}
                  </Stack>

                  {(ad?.modifiedByUser || ad?.lastModifiedDate) && (
                    <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mt: 1.5, fontSize: 11 }}>
                      Last updated
                      {ad?.modifiedByUser ? ` by ${ad.modifiedByUser}` : ''}
                      {ad?.lastModifiedDate ? ` · ${formatDate1(ad.lastModifiedDate, 'dd MMM, yyyy HH:mm')}` : ''}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })()}
        {order?.appointmentDeliveryHistory?.length > 0 && (
          <Grid paddingBottom={2}>
            <Card>
              <CardHeader
                title={`Previous Appointments (${order.appointmentDeliveryHistory.length})`}
                sx={{ fontWeight: 800 }}
              />
              <CardContent>
                <AppointmentHistoryView rowData={order} />
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* reverse shipment blocks */}
        {isReverseShipment ? (
          <Grid container spacing={2} paddingBottom={2} alignItems='stretch'>
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  height: '100%',
                  borderRadius: '14px',
                  border: '1px solid #DFE3E8',
                  boxShadow: 'none',
                  overflow: 'hidden',
                  '& > .MuiCardHeader-root': { py: 1.25, px: 2.5, bgcolor: '#F9FAFB', borderBottom: '1px solid #DFE3E8' },
                  '& > .MuiCardHeader-root .MuiCardHeader-title': { fontFamily: 'Poppins, sans-serif', fontSize: 13, fontWeight: 700, color: '#212B36' },
                  '& > .MuiCardHeader-root .MuiCardHeader-action': { m: 0, alignSelf: 'center' },
                  '& .MuiCardHeader-action .MuiButtonBase-root': { textTransform: 'none', fontWeight: 600, fontSize: 12, py: 0.3, px: 1.5, boxShadow: 'none' },
                  '& > .MuiCardContent-root': { px: 2.5, py: 1.5, pb: '16px !important' },
                }}
              >
                <PIIDetails
                  merchantOid={order?.merchantDetail?.key}
                  reference={order?.reference}
                  awbNumber={order?.awb_number}
                  title='Customer Details'
                  isReverseShipment
                  fallbackComponent={({ email, phone }) => (
                    <>
                      <AddressLine label='Pickup Name' value={order?.pickup_details?.from_name} />
                      <AddressLine label='Phone Number' value={phone} />
                      <AddressLine label='Email ID' value={email} />
                      <AddressLine
                        label='Address'
                        multiline
                        value={
                          shouldMaskDataReverse(report, order, piiAccessAllow)
                            ? maskAddress(addressStringWithPrefix('from', order?.pickup_details))
                            : addressStringWithPrefix('from', order?.pickup_details)
                        }
                      />
                    </>
                  )}
                />
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <AddressCard title='Warehouse Details'>
                <AddressLine label='Customer Name' value={order?.delivery_details?.to_name} />
                <AddressLine
                  label='Phone Number'
                  value={
                    shouldMaskData(report, order, piiAccessAllow)
                      ? maskPhoneNumber(order?.delivery_details?.to_phone_number)
                      : order?.delivery_details?.to_phone_number
                  }
                />
                <AddressLine
                  label='Email ID'
                  value={
                    shouldMaskData(report, order, piiAccessAllow)
                      ? maskEmail(order?.delivery_details?.to_email)
                      : order?.delivery_details?.to_email
                  }
                />
                <AddressLine
                  label='Shipping Address'
                  multiline
                  value={
                    shouldMaskData(report, order, piiAccessAllow)
                      ? maskAddress(addressStringWithPrefix('to', order?.delivery_details))
                      : addressStringWithPrefix('to', order?.delivery_details)
                  }
                  trailing={
                    odaResult && (
                      <Tooltip
                        title={`Destination Pincode falls under ODA service area${odaResult?.cp ? ` (${odaResult.cp}${odaResult?.odaValue ? ` — ${odaResult.odaValue}` : ''})` : ''}.`}
                      >
                        <Chip
                          label='ODA : true'
                          color='warning'
                          size='small'
                          sx={{ ml: 1, mt: 0.5 }}
                        />
                      </Tooltip>
                    )
                  }
                />
              </AddressCard>
            </Grid>
          </Grid>
        ) : (
          <Grid container spacing={2} paddingBottom={2} alignItems='stretch'>
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  height: '100%',
                  borderRadius: '14px',
                  border: '1px solid #DFE3E8',
                  boxShadow: 'none',
                  overflow: 'hidden',
                  '& > .MuiCardHeader-root': { py: 1.25, px: 2.5, bgcolor: '#F9FAFB', borderBottom: '1px solid #DFE3E8' },
                  '& > .MuiCardHeader-root .MuiCardHeader-title': { fontFamily: 'Poppins, sans-serif', fontSize: 13, fontWeight: 700, color: '#212B36' },
                  '& > .MuiCardHeader-root .MuiCardHeader-action': { m: 0, alignSelf: 'center' },
                  '& .MuiCardHeader-action .MuiButtonBase-root': { textTransform: 'none', fontWeight: 600, fontSize: 12, py: 0.3, px: 1.5, boxShadow: 'none' },
                  '& > .MuiCardContent-root': { px: 2.5, py: 1.5, pb: '16px !important' },
                }}
              >
                <PIIDetails
                  merchantOid={order?.merchantDetail?.key}
                  reference={order?.reference}
                  awbNumber={order?.awb_number}
                  title='Customer Details'
                  isReverseShipment={false}
                  fallbackComponent={({ email, phone }) => (
                    <>
                      <AddressLine
                        label='Customer Name'
                        value={order?.delivery_details?.to_name}
                      />
                      <AddressLine label='Phone Number' value={phone} />
                      <AddressLine label='Email ID' value={email} />
                      <AddressLine
                        label='Shipping Address'
                        multiline
                        value={
                          shouldMaskData(report, order, piiAccessAllow)
                            ? maskAddress(addressStringWithPrefix('to', order?.delivery_details))
                            : addressStringWithPrefix('to', order?.delivery_details)
                        }
                        trailing={
                          odaResult && (
                            <Tooltip
                              title={`Destination Pincode falls under ODA service area${odaResult?.cp ? ` (${odaResult.cp}${odaResult?.odaValue ? ` — ${odaResult.odaValue}` : ''})` : ''}.`}
                            >
                              <Chip
                                label='ODA : true'
                                color='warning'
                                size='small'
                                sx={{ ml: 1, mt: 0.5 }}
                              />
                            </Tooltip>
                          )
                        }
                      />
                    </>
                  )}
                  orderDetails={order}
                />
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <AddressCard title='Pickup Details'>
                <AddressLine label='Picker Name' value={order?.pickup_details?.from_name} />
                <AddressLine
                  label='Phone Number'
                  value={
                    shouldMaskDataReverse(report, order, piiAccessAllow)
                      ? maskPhoneNumber(order?.pickup_details?.from_phone_number)
                      : order?.pickup_details?.from_phone_number
                  }
                />
                <AddressLine
                  label='Email ID'
                  value={
                    shouldMaskDataReverse(report, order, piiAccessAllow)
                      ? maskEmail(order?.pickup_details?.from_email)
                      : order?.pickup_details?.from_email
                  }
                />
                <AddressLine
                  label='Pickup Address'
                  multiline
                  value={
                    shouldMaskDataReverse(report, order, piiAccessAllow)
                      ? maskAddress(addressStringWithPrefix('from', order?.pickup_details))
                      : addressStringWithPrefix('from', order?.pickup_details)
                  }
                />
              </AddressCard>
            </Grid>
            <Grid item xs={12} md={4}>
              <AddressCard title='Return Details'>
                <AddressLine label='Return Name' value={order?.return_details?.name} />
                <AddressLine
                  label='Phone Number'
                  value={
                    shouldMaskDataReverse(report, order, piiAccessAllow)
                      ? maskPhoneNumber(order?.return_details?.phone_number)
                      : order?.return_details?.phone_number
                  }
                />
                <AddressLine
                  label='Email ID'
                  value={
                    shouldMaskDataReverse(report, order, piiAccessAllow)
                      ? maskEmail(order?.return_details?.email)
                      : order?.return_details?.email
                  }
                />
                <AddressLine
                  label='Return Address'
                  multiline
                  value={
                    shouldMaskDataReverse(report, order, piiAccessAllow)
                      ? maskAddress(
                          [
                            order?.return_details?.address,
                            order?.return_details?.city,
                            order?.return_details?.pincode,
                            order?.return_details?.state,
                          ].join(', ')
                        )
                      : [
                          order?.return_details?.address,
                          order?.return_details?.city,
                          order?.return_details?.pincode,
                          order?.return_details?.state,
                        ].join(', ')
                  }
                />
              </AddressCard>
            </Grid>
          </Grid>
        )}

        <Grid paddingBottom={2}>
          <Card>
            <CardHeader title='Product Details' />
            <CardContent>
              <DataGrid
                columns={columns}
                rows={
                  order && order?.item_list
                    ? order?.item_list.map((val, index) => ({
                        ...val,
                        id: index,
                        productImages: skuImageMap[val?.sku_id] || [],
                      }))
                    : []
                }
                getRowId={(row) => row?.id}
                hideFooterPagination={true}
                autoHeight
                disableColumnMenu
                disableColumnFilter
                disableSelectionOnClick
                sx={{
                  border: 'none',
                  fontFamily: 'Poppins, sans-serif',
                  '& .MuiDataGrid-columnHeaders': { backgroundColor: '#F9FAFB', borderBottom: '1px solid #DFE3E8' },
                  '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 700, color: '#212B36', fontSize: 12.5 },
                  '& .MuiDataGrid-cell': { fontSize: 12.5, color: '#212B36' },
                }}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid paddingBottom={2}>
          <Card>
            <CardHeader title='Timeline' />
            <Grid sx={{ padding: 6, overflow: 'scroll' }}>
              {/* <OrderTimeline orderDetailInfo={order ? order : []} /> */}
              <OrderTimeline
                orderDetailInfo={order ? order : []}
                cancelledBy={order?.cancelledBy}
                awb={order?.awb_number}
              />
            </Grid>
          </Card>
        </Grid>
        <CarrierLogsSection reference={order?.reference} />
        {order && order?.pt_order_history && (
          <Grid paddingBottom={2}>
            <Card>
              <CardHeader title='PT Order History Timeline' />
              <Grid sx={{ padding: 6, overflow: 'scroll' }}>
                {/* <OrderTimeline orderDetailInfo={order ? order : []} /> */}
                <PTOrderTimeline
                  orderDetailInfo={order ? order : []}
                  cancelledBy={order?.cancelledBy}
                  awb={order?.awb_number}
                />
              </Grid>
            </Card>
          </Grid>
        )}
        {order && order?.ndrData && (
          <Grid item xs={12} paddingBottom={2}>
            <Card>
              <CardHeader title='NDR Details' sx={{ fontWeight: 800 }} />
              <CardContent>
                <Grid container spacing={1}>
                  <Grid item xs={6} md={3}>
                    <Typography variant='subtitle2' color={'black'}>
                      NDR Reported Date
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant='subtitle2'>
                      {order && order.ndrData ? formatDate(order.ndrData?.ndrReportedDate) : ''}{' '}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant='subtitle2' color={'black'}>
                      NDR Re-reported Date
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant='subtitle2'>
                      {order && order.ndrData ? formatDate(order.ndrData?.ndrRereportedDate) : ''}{' '}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant='subtitle2' color={'black'}>
                      Current NDR Status
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant='subtitle2'>
                      {order && order.ndrData ? (
                        <Chip size='small' color='primary' label={order.ndrData?.ndrStatus} />
                      ) : (
                        ''
                      )}{' '}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant='subtitle2' color={'black'}>
                      NDR Age
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant='subtitle2'>
                      {order && order.ndrData?.ndrAge ? order?.ndrData?.ndrAge + ' days' : '-'}{' '}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant='subtitle2' color={'black'}>
                      OFD Count
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant='subtitle2'>
                      {order && order.ndrData?.ofdCount ? order.ndrData?.ofdCount : '-'}{' '}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant='subtitle2' color={'black'}>
                      Reattempt Count
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant='subtitle2'>
                      {order && order?.ndrData?.reAttemptCount
                        ? order.ndrData?.reAttemptCount
                        : '-'}{' '}
                    </Typography>
                  </Grid>

                  {ndrActionLog && ndrActionLog?.length > 0 && (
                    <>
                      <br />
                      <Grid item xs={12}>
                        <Typography
                          variant='subtitle2'
                          sx={{ textUnderlinePosition: 'auto' }}
                          color={'black'}
                        >
                          NDR Action Log{' '}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography
                          variant='subtitle2'
                          sx={{ textUnderlinePosition: 'auto' }}
                          color={'black'}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography
                          variant='subtitle2'
                          sx={{ textUnderlinePosition: 'auto' }}
                          color={'black'}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography
                          variant='subtitle2'
                          sx={{ textUnderlinePosition: 'auto' }}
                          color={'black'}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <ProzoDataGrid
                          columns={columnsNDRActionLog}
                          getRowId={(row) => row?.id}
                          rows={
                            ndrActionLog.length > 0
                              ? ndrActionLog.map((val, index) => ({ ...val, id: index }))
                              : []
                          }
                          hideFooterPagination={true}
                          sizePerPage={sizePerPage}
                          setSizePerPage={setSizePerPage}
                          setPage={setPage}
                          page={page}
                          pagination={true}
                          rowCount={ndrActionLog?.length || 0}
                          autoHeight
                        />
                      </Grid>
                    </>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}
        {ndrCalls?.length > 0 && (
          <Grid item xs={12} paddingBottom={2}>
            <Card sx={{ paddingBottom: '2' }}>
              <CardHeader title='NDR Call Log' sx={{ fontWeight: 800 }} />
              <CardContent>
                <ProzoDataGrid
                  columns={ndrCallcolumns}
                  autoHeight={true}
                  rows={ndrCalls || []}
                  pagination={false}
                  hideFooterPagination={true}
                  rowHeight={120}
                  getRowId={(row) => row?.awb_number + row?.created_at}
                />
              </CardContent>
            </Card>
          </Grid>
        )}
        {(order?.shipment_type === 'B2B' || order?.shipmentType === 'B2B') &&
          order?.rtoRequestDetails && (
            <Grid item xs={12} paddingBottom={2}>
              <Card sx={{ borderLeft: '4px solid', borderColor: 'warning.main' }}>
                <CardHeader
                  avatar={<AssignmentReturnIcon color='warning' />}
                  title='RTO Request Details'
                  titleTypographyProps={{ fontWeight: 800 }}
                />
                <Divider />
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Stack spacing={0.25}>
                        <Typography variant='caption' color='text.secondary'>
                          RTO Reason
                        </Typography>
                        <Typography variant='body2' fontWeight={600}>
                          {order?.rtoRequestDetails?.rtoReasonLabel ||
                            order?.rtoRequestDetails?.rtoReason ||
                            '-'}
                        </Typography>
                      </Stack>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Stack spacing={0.25}>
                        <Typography variant='caption' color='text.secondary'>
                          Requested By
                        </Typography>
                        <Typography variant='body2' fontWeight={600}>
                          {order?.rtoRequestDetails?.rtoRequestedBy || '-'}
                        </Typography>
                      </Stack>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Stack spacing={0.25}>
                        <Typography variant='caption' color='text.secondary'>
                          Requested Date
                        </Typography>
                        <Typography variant='body2' fontWeight={600}>
                          {formatDate(order?.rtoRequestDetails?.rtoRequestedDate)}
                        </Typography>
                      </Stack>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Stack spacing={0.5} alignItems='flex-start'>
                        <Typography variant='caption' color='text.secondary'>
                          Courier Email Sent
                        </Typography>
                        <Chip
                          size='small'
                          color={
                            order?.rtoRequestDetails?.rtoCourierEmailSent ? 'success' : 'default'
                          }
                          label={order?.rtoRequestDetails?.rtoCourierEmailSent ? 'Yes' : 'No'}
                        />
                      </Stack>
                    </Grid>
                    <Grid item xs={12}>
                      <Divider sx={{ mb: 1.5 }} />
                      <Stack spacing={0.25}>
                        <Typography variant='caption' color='text.secondary'>
                          Remarks
                        </Typography>
                        <Typography variant='body2' fontWeight={600}>
                          {order?.rtoRequestDetails?.rtoRemarks || '-'}
                        </Typography>
                      </Stack>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          )}
        {order && order?.futworkDetails?.futworkLogs && (
          <Grid item xs={12} paddingBottom={2}>
            <Card>
              <CardHeader title='Futworks Lead Details' sx={{ fontWeight: 800 }} />
              <CardContent>
                {/* Feedback item 5: ProzoDataGrid must NOT be wrapped directly in a bare
                    <Grid container> — MUI's DataGrid measures its immediate parent's width via
                    ResizeObserver, and an unwrapped flex child of a container (no <Grid item>)
                    has no defined width, so the grid silently renders at 0px ("MUI:
                    useResizeContainer - parent DOM element has an empty width"). This made the
                    card header show but the table appear completely blank. Render the grid
                    directly under CardContent instead, matching the working NDR Call Log card. */}
                <ProzoDataGrid
                  columns={columnsFutworkDate}
                  getRowId={(row) => row?.id}
                  rows={
                    order?.futworkDetails?.futworkLogs?.length > 0
                      ? order?.futworkDetails?.futworkLogs.map((val, index) => ({
                        ...val,
                        id: index,
                      }))
                      : []
                  }
                  hideFooterPagination={true}
                  rowCount={ndrActionLog?.length || 0}
                  autoHeight
                />
              </CardContent>
            </Card>
          </Grid>
        )}
        {order && order?.growupNextDetails?.growupNextLogs && (
          <Grid item xs={12} paddingBottom={2}>
            <Card>
              <CardHeader title='Growup Next Details' sx={{ fontWeight: 800 }} />
              <CardContent>
                <ProzoDataGrid
                  columns={columnsFutworkDate}
                  getRowId={(row) => row?.id}
                  rows={
                    order?.growupNextDetails?.growupNextLogs?.length > 0
                      ? order?.growupNextDetails?.growupNextLogs.map((val, index) => ({
                        ...val,
                        id: index,
                      }))
                      : []
                  }
                  hideFooterPagination={true}
                  rowCount={ndrActionLog?.length || 0}
                  autoHeight
                />
              </CardContent>
            </Card>
          </Grid>
        )}
        {/* Feedback item 5: orders routed to the newer Futwork AI calling provider (PROSHIP-1010,
            2026-03-06) store their lead-push history under futworkAIDetails.futworkAILogs — a
            separate field from the classic futworkDetails/growupNextDetails above. This card was
            missing, so those orders' "lead push timestamp" appeared blank even though the data
            exists in Mongo. */}
        {order && order?.futworkAIDetails?.futworkAILogs && (
          <Grid item xs={12} paddingBottom={2}>
            <Card>
              <CardHeader title='Futwork AI Lead Details' sx={{ fontWeight: 800 }} />
              <CardContent>
                <ProzoDataGrid
                  columns={columnsFutworkDate}
                  getRowId={(row) => row?.id}
                  rows={
                    order?.futworkAIDetails?.futworkAILogs?.length > 0
                      ? order?.futworkAIDetails?.futworkAILogs.map((val, index) => ({
                        ...val,
                        id: index,
                      }))
                      : []
                  }
                  hideFooterPagination={true}
                  rowCount={ndrActionLog?.length || 0}
                  autoHeight
                />
              </CardContent>
            </Card>
          </Grid>
        )}
        {order && order?.nprData && (
          <Grid item xs={12} paddingBottom={2}>
            <Card>
              <CardHeader title='NPR Details' sx={{ fontWeight: 800 }} />
              <CardContent>
                <Grid container spacing={1}>
                  <Grid item xs={6} md={3}>
                    <Typography variant='subtitle2' color={'black'}>
                      NPR Reported Date
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant='subtitle2'>
                      {order && order.nprData ? formatDate(order.nprData?.nprReportedDate) : ''}{' '}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant='subtitle2' color={'black'}>
                      NPR Re-reported Date
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant='subtitle2'>
                      {order && order.nprData ? formatDate(order.nprData?.nprRereportedDate) : ''}{' '}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant='subtitle2' color={'black'}>
                      Current NPR Status
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant='subtitle2'>
                      {order && order.nprData ? (
                        <Chip size='small' color='primary' label={order.nprData?.nprStatus} />
                      ) : (
                        ''
                      )}{' '}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant='subtitle2' color={'black'}>
                      NPR Age
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant='subtitle2'>
                      {order && order.nprData ? order.nprData?.nprAge + ' days' : ''}{' '}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant='subtitle2' color={'black'}>
                      OFP Count
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant='subtitle2'>
                      {order && order.nprData ? order.nprData?.ofPCount : ''}{' '}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant='subtitle2' color={'black'}>
                      Reattempt Count
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant='subtitle2'>
                      {order && order.nprData ? order.nprData?.reAttemptCount : ''}{' '}
                    </Typography>
                  </Grid>

                  {nprActionLog && nprActionLog?.length > 0 && (
                    <>
                      <br />
                      <Grid item xs={12}>
                        <Typography
                          variant='subtitle2'
                          sx={{ textUnderlinePosition: 'auto' }}
                          color={'black'}
                        >
                          NDR Action Log{' '}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography
                          variant='subtitle2'
                          sx={{ textUnderlinePosition: 'auto' }}
                          color={'black'}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography
                          variant='subtitle2'
                          sx={{ textUnderlinePosition: 'auto' }}
                          color={'black'}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography
                          variant='subtitle2'
                          sx={{ textUnderlinePosition: 'auto' }}
                          color={'black'}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <ProzoDataGrid
                          columns={columnsNPRActionLog}
                          getRowId={(row) => row?.id}
                          rows={
                            nprActionLog.length > 0
                              ? nprActionLog.map((val, index) => ({ ...val, id: index }))
                              : []
                          }
                          hideFooterPagination={true}
                          sizePerPage={sizePerPage}
                          setSizePerPage={setSizePerPage}
                          setPage={setPage}
                          page={page}
                          pagination={true}
                          rowCount={nprActionLog?.length || 0}
                          autoHeight
                        />
                      </Grid>
                    </>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}
        {nprCalls?.length > 0 && (
          <Grid item xs={12} paddingBottom={2}>
            <Card sx={{ paddingBottom: '2' }}>
              <CardHeader title='NPR Call Log' sx={{ fontWeight: 800 }} />
              <CardContent>
                <ProzoDataGrid
                  columns={nprCallcolumns}
                  autoHeight={true}
                  rows={nprCalls || []}
                  pagination={false}
                  hideFooterPagination={true}
                  rowHeight={120}
                  getRowId={(row) => row?.awb_number + row?.created_at}
                />
              </CardContent>
            </Card>
          </Grid>
        )}
       
      <MUIModal
        open={ndrQModal}
        title='Call Details'
        maxWidth={'lg'}
        handleClose={() => setNdrQModal(false)}
      >
        <ProzoDataGrid
          // autoHeight={false}
          columns={[
            {
              field: 'question',
              headerName: 'Question',
              flex: 3,
            },
            {
              field: 'answer',
              flex: 1,
              headerName: 'Answer',
            },
          ]}
          autoHeight={true}
          rows={ndrQ || []}
          pagination={false}
          hideFooterPagination={true}
          rowHeight={120}
          getRowId={(row) => Math.random()}
        />
      </MUIModal>
      <MUIModal
        open={sorterImagesModal}
        handleClose={() => setSorterImagesModal(false)}
        maxWidth='md'
        title='Sorter Images'
        children={
          <Box
            sx={{
              p: 2,
              maxHeight: '70vh',
              overflow: 'auto',
            }}
          >
            <Grid container spacing={2}>
              {order?.sorterImages?.map((image, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      '&:hover': {
                        boxShadow: 3,
                      },
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Typography
                        variant='subtitle2'
                        sx={{
                          mb: 1,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          fontWeight: 500,
                        }}
                      >
                        {image.fileName}
                      </Typography>
                      <Button
                        variant='contained'
                        color='primary'
                        size='small'
                        fullWidth
                        startIcon={<VisibilityIcon />}
                        onClick={async () => {
                          const { data } = await postGeneric(apiConstants.REFRESH_FILE_URL, {
                            signedUrl: image.s3Url.signedLink,
                          })
                          window.open(data, '_blank')
                        }}
                      >
                        View Image
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        }
      />
      <MUIModal
        open={otpCancellationModal}
        handleClose={() => setOtpCancellationModal(false)}
        maxWidth='md'
        title='OTP Cancellation Details'
        children={
          <Box sx={{ p: 2 }}>
            {loadingOtpDetails ? (
              <Loader />
            ) : otpCancellationDetails && otpCancellationDetails?.length > 0 ? (
              <Grid container spacing={2}>
                {otpCancellationDetails?.map((detail, index) => (
                  <Grid item xs={12} key={index}>
                    <Card sx={{ mb: 2 }}>
                      <CardContent>
                        <Grid container spacing={2}>
                          <Grid item xs={6} md={4}>
                            <Typography variant='subtitle2' color='textSecondary'>
                              AWB Number
                            </Typography>
                            <Typography variant='body2'>{detail.awbNumber || '-'}</Typography>
                          </Grid>
                          <Grid item xs={6} md={4}>
                            <Typography variant='subtitle2' color='textSecondary'>
                              Timestamp
                            </Typography>
                            <Typography variant='body2'>
                              {detail.timestamp ? formatDate(detail.timestamp) : '-'}
                            </Typography>
                          </Grid>
                          <Grid item xs={6} md={4}>
                            <Typography variant='subtitle2' color='textSecondary'>
                              Remark
                            </Typography>
                            <Typography variant='body2'>{detail.remark || '-'}</Typography>
                          </Grid>
                          <Grid item xs={6} md={4}>
                            <Typography variant='subtitle2' color='textSecondary'>
                              Location
                            </Typography>
                            <Typography variant='body2'>{detail.location || '-'}</Typography>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <Typography>No cancellation details found.</Typography>
              </Box>
            )}
          </Box>
        }
      />

      {/* NETRA scores — admin always shows them. Address quality + RTO risk, at the bottom. */}
      {(order?.netra_address || order?.netra_rto) && (
        <Grid container spacing={2} paddingBottom={2}>
          {order?.netra_address && (
            <Grid item xs={12} md={6}>
              <AddressQualityCard netra={order.netra_address} orderId={order.id} />
            </Grid>
          )}
          {order?.netra_rto && (
            <Grid item xs={12} md={6}>
              <RtoRiskCard netra={order.netra_rto} orderId={order.id} />
            </Grid>
          )}
        </Grid>
      )}
      </Box>
    </Page>
  )
}
